import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { registerPlugin } from '@capacitor/core';
import { identify, resetAnalytics } from '../analytics';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import type { User } from '../types';
import { removePushToken } from '../services/pushNotificationService';
import { getLocalPhone } from '../utils/localPhone';

interface AppleSignInResponse {
  response: {
    identityToken: string;
    email?: string;
    givenName?: string;
    familyName?: string;
    user?: string;
  };
}

const AppleSignIn = registerPlugin<{
  authorize(): Promise<AppleSignInResponse>;
}>('AppleSignIn');

type ProfileRow = Database['public']['Tables']['profiles']['Row'];

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  hasProfile: boolean;

  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  verifyOtp: (email: string, token: string) => Promise<{ error: string | null }>;
  resendOtp: (email: string) => Promise<{ error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signInWithApple: () => Promise<{ error: string | null }>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  deleteAccount: () => Promise<{ error: string | null }>;
  passwordRecovery: boolean;
  clearPasswordRecovery: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  const fetchProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single<ProfileRow>();

    if (error || !data) {
      // No profile row — let user proceed to ProfileSetupScreen
      setUser(null);
      setHasProfile(false);
      return;
    }

    const profile: User = {
      id: data.id,
      handle: data.handle,
      name: data.name,
      airlineCode: data.airline_code,
      baseAirport: data.base_airport,
      avatarUrl: data.avatar_url ?? undefined,
      phone: getLocalPhone(data.id),
      hasPhone: !!(data as Record<string, unknown>).phone_hash,
      tier: (data.tier === 'premium' ? 'premium' : 'free') as User['tier'],
      ...(data.tier_expires_at ? { tierExpiresAt: data.tier_expires_at } : {}),
    };

    setUser(profile);
    setHasProfile(true);
    identify(profile.id, { handle: profile.handle, airline: profile.airlineCode, tier: profile.tier });
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!session?.user?.id) return;
    await fetchProfile(session.user.id);
  }, [session, fetchProfile]);

  // Listen for auth state changes
  useEffect(() => {
    let initializing = true;

    // Get initial session — catch network errors so the app doesn't crash
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      if (initialSession?.user) {
        fetchProfile(initialSession.user.id).finally(() => {
          setLoading(false);
          initializing = false;
        });
      } else {
        // Check if we had a stored session (token refresh may have failed temporarily,
        // e.g. after iOS kills WKWebView content process during share extension use).
        // Keep loading=true so we show spinner instead of login — onAuthStateChange
        // will deliver the session once the token refresh succeeds.
        const storageKey = `sb-${new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split('.')[0]}-auth-token`;
        const hasStoredSession = !!localStorage.getItem(storageKey);
        if (hasStoredSession) {
          // Fallback: if onAuthStateChange never fires, show login after 5s
          setTimeout(() => {
            initializing = false;
            setLoading(false);
          }, 5000);
        } else {
          setLoading(false);
          initializing = false;
        }
      }
    }).catch((err) => {
      console.warn('[AuthContext] getSession failed (offline?):', err);
      setLoading(false);
      initializing = false;
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Always detect password recovery, even during initialization
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
      }

      // Skip if still initializing — getSession already handles the first session
      if (initializing) return;

      setSession(newSession);

      if (newSession?.user) {
        setLoading(true);
        fetchProfile(newSession.user.id).finally(() => setLoading(false));
      } else {
        setUser(null);
        setHasProfile(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signUp = useCallback(
    async (email: string, password: string): Promise<{ error: string | null }> => {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) return { error: error.message };
      // Supabase fakes success when email already exists (returns empty identities)
      if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
        return { error: 'An account with this email already exists. Try signing in instead.' };
      }
      return { error: null };
    },
    []
  );

  const verifyOtp = useCallback(
    async (email: string, token: string): Promise<{ error: string | null }> => {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'signup',
      });
      if (error) return { error: error.message };
      return { error: null };
    },
    []
  );

  const resendOtp = useCallback(
    async (email: string): Promise<{ error: string | null }> => {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      if (error) return { error: error.message };
      return { error: null };
    },
    []
  );

  const signIn = useCallback(
    async (emailOrHandle: string, password: string): Promise<{ error: string | null }> => {
      let email = emailOrHandle;

      // If no @ or starts with @ (handle prefix), treat as handle
      if (!emailOrHandle.includes('@') || emailOrHandle.startsWith('@')) {
        const handle = emailOrHandle.replace(/^@/, '');
        const { data, error: rpcError } = await supabase.rpc(
          'get_email_by_handle' as never,
          { lookup_handle: handle } as never,
        );

        if (rpcError || !data) {
          return { error: 'No account found with that username.' };
        }
        email = data as string;
      }

      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        if (error.message === 'Invalid login credentials') {
          return { error: 'Incorrect email or password.' };
        }
        if (error.message === 'Email not confirmed') {
          return { error: 'Your email hasn\'t been verified yet. Check your inbox for a verification code.' };
        }
        return { error: error.message };
      }
      return { error: null };
    },
    []
  );

  const signInWithApple = useCallback(async (): Promise<{ error: string | null }> => {
    try {
      const { response } = await AppleSignIn.authorize();

      const { error } = await supabase.auth.signInWithIdToken({
        provider: 'apple',
        token: response.identityToken,
      });

      if (error) return { error: error.message };
      return { error: null };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      // User cancelled — not an error
      if (/cancel/i.test(msg)) return { error: null };
      return { error: msg || 'Apple Sign In failed' };
    }
  }, []);

  const resetPassword = useCallback(
    async (email: string): Promise<{ error: string | null }> => {
      const isNative = typeof window !== 'undefined' && window.location.protocol === 'capacitor:';
      const redirectTo = isNative
        ? 'rostfound://reset-password'
        : `${window.location.origin}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });
      if (error) return { error: error.message };
      return { error: null };
    },
    []
  );

  const updatePassword = useCallback(
    async (newPassword: string): Promise<{ error: string | null }> => {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) return { error: error.message };
      return { error: null };
    },
    []
  );

  const deleteAccount = useCallback(async (): Promise<{ error: string | null }> => {
    if (!session?.user?.id) return { error: 'Not signed in' };

    // Call edge function — uses service_role to delete profile + auth.users row
    const { data, error: fnError } = await supabase.functions.invoke('delete-account');

    if (fnError) return { error: fnError.message };
    if (data?.error) return { error: data.error };

    // Clean up local data
    try { localStorage.clear(); } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }

    // Sign out locally
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setHasProfile(false);

    return { error: null };
  }, [session]);

  const signOut = useCallback(async () => {
    // Clear APNs token before signing out so the device stops receiving pushes
    if (session?.user?.id) {
      await removePushToken(session.user.id).catch(() => {});
    }
    await supabase.auth.signOut();
    resetAnalytics();
    setSession(null);
    setUser(null);
    setHasProfile(false);
  }, [session]);

  const clearPasswordRecovery = useCallback(() => setPasswordRecovery(false), []);

  const value: AuthContextValue = {
    session,
    user,
    loading,
    hasProfile,
    signUp,
    verifyOtp,
    resendOtp,
    signIn,
    signInWithApple,
    resetPassword,
    updatePassword,
    signOut,
    refreshProfile,
    deleteAccount,
    passwordRecovery,
    clearPasswordRecovery,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
